import { EMPTY_VALUE } from "staticConfiguration";
import type {
  DataGridProProps,
  GridColDef,
  GridColumnVisibilityModel,
  GridEventListener,
} from "@mui/x-data-grid-pro";
import type { BrandClassification } from "core/entities/brandDefs";
import { useDebouncedCallback } from "core/hooks/useDebouncedCallback";
import { useFormatCurrency } from "core/hooks/useFormatCurrency";
import { useFormatDate } from "core/hooks/useFormatDate";
import { useIsShopUser } from "core/hooks/useIsShopUser";
import { useLocalStorage } from "core/hooks/useLocalStorage";
import { getBrand } from "core/utils/getBrand";
import compare from "just-compare";
import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  DEFAULT_INVOICE_DATE_STRING,
  type PaymentRequestDto,
} from "../entities";
import { usePaymentRequestEnumTranslations } from "./usePaymentRequestEnumTranslations";

const KEY_BASE = "payment-request" as const;
const COLUMN_VISIBILITY_STORAGE_KEY =
  `${KEY_BASE}_column-visibility-model` as const;
const COLUMN_WIDTHS_STORAGE_KEY = `${KEY_BASE}_column-widths` as const;
const COLUMN_ORDER_STORAGE_KEY = `${KEY_BASE}_column-order` as const;

const DEFAULT_COLUMN_SORT_ORDER: string[] = [];
const DEFAULT_CUSTOM_COLUMN_WIDTHS: Record<string, number> = {};
const DEFAULT_COLUMN_VISIBILITY_MODEL: GridColumnVisibilityModel = {
  humanReadableId: true,
  requestedAmount: true,
  fileClaimNumber: true,
  shopDetails: true,
  applicantName: true,
  insurerName: true,
  totalOriginalAmount: true,
  clerkName: true,
  cycleTime: true,
  status: true,
  requestedOn: true,
  invoiceDate: true,
  lastModificationTime: true,
  adjusterName: false,
  insuredName: false,
  closureStatus: false,
  amountDueFromInsurer: false,
  requestedAmountType: false,
  requestReason: false,
};

export function usePaymentRequestDataGridColumnProps() {
  const { brandClassification } = getBrand();
  const { t: tHeader } = useTranslation("PaymentRequest", {
    keyPrefix: "Next:PaymentRequest:GridHeader",
  });
  const { t: tValue } = useTranslation("PaymentRequest", {
    keyPrefix: "Next:PaymentRequest:GridValue",
  });
  const enumTranslations = usePaymentRequestEnumTranslations();
  const isShopUser = useIsShopUser();

  const [columnSortOrder, setColumnSortOrder] = useLocalStorage<string[]>(
    COLUMN_ORDER_STORAGE_KEY,
    DEFAULT_COLUMN_SORT_ORDER
  );

  const [customWidths, setCustomWidths] = useLocalStorage<
    Record<string, number>
  >(COLUMN_WIDTHS_STORAGE_KEY, DEFAULT_CUSTOM_COLUMN_WIDTHS);

  const [columnVisibilityModel, setColumnVisibilityModel] =
    useLocalStorage<GridColumnVisibilityModel>(
      COLUMN_VISIBILITY_STORAGE_KEY,
      DEFAULT_COLUMN_VISIBILITY_MODEL
    );

  const areColumnsCustomized = Boolean(
    !compare(columnSortOrder, DEFAULT_COLUMN_SORT_ORDER) ||
      !compare(customWidths, DEFAULT_CUSTOM_COLUMN_WIDTHS) ||
      !compare(columnVisibilityModel, DEFAULT_COLUMN_VISIBILITY_MODEL)
  );
  const baseFormatCurrency = useFormatCurrency({
    style: "currency",
    currency: "CAD",
    currencyDisplay: "narrowSymbol",
  });
  const baseFormatDate = useFormatDate({
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });

  const formatCurrency = useCallback<NonNullable<GridColDef["valueFormatter"]>>(
    (value) => {
      if (value === null) {
        return EMPTY_VALUE;
      }
      try {
        return baseFormatCurrency(value);
      } catch {
        return EMPTY_VALUE;
      }
    },
    [baseFormatCurrency]
  );
  const formatDate = useCallback<NonNullable<GridColDef["valueFormatter"]>>(
    (value) => {
      if (
        value === null ||
        typeof value === "undefined" ||
        value === DEFAULT_INVOICE_DATE_STRING
      ) {
        return EMPTY_VALUE;
      }
      try {
        return baseFormatDate(value);
      } catch {
        return EMPTY_VALUE;
      }
    },
    [baseFormatDate]
  );

  const initialColumns = useMemo(() => {
    const insuranceHeaderMap: Record<BrandClassification, string> = {
      Collision: tHeader("insuranceCompany_collision"),
      Glass: tHeader("insuranceCompany_glass"),
      Mechanical: tHeader("insuranceCompany_mechanical"),
      Home: tHeader("insuranceCompany_home"),
    };
    const columnDefs: GridColDef<PaymentRequestDto>[] = [
      {
        field: "humanReadableId",
        headerName: tHeader("humanReadableId"),
        width: 100,
      },
      {
        field: "fileClaimNumber",
        headerName: tHeader("fileClaimNumber"),
        flex: 1,
      },
      {
        field: "shopDetails",
        headerName: tHeader("shopDetails"),
        valueGetter: (_, { shopName, shopCode }) => {
          if (!shopName && !shopCode) {
            return EMPTY_VALUE;
          }
          return `(${shopCode}) ${shopName}`;
        },

        flex: 1,
      },
      { field: "applicantName", headerName: tHeader("applicantName"), flex: 1 },
      {
        field: "insurerName",
        headerName: insuranceHeaderMap[brandClassification],
        flex: 1,
      },
      {
        field: "clerkName",
        headerName: tHeader("clerkName"),
        flex: 1,
        valueGetter: (_, { clerkId, clerkFirstName, clerkLastName }) => {
          if (!clerkId) {
            return EMPTY_VALUE;
          }
          return (
            [clerkFirstName, clerkLastName].filter(Boolean).join(" ") ||
            EMPTY_VALUE
          );
        },
      },
      {
        field: "cycleTime",
        headerName: tHeader("cycleTime"),
        valueGetter: (_, { cycleTime }) =>
          tValue("cycleTime", { days: cycleTime }),
        flex: 1,
      },
      {
        field: "requestedAmount",
        headerName: tHeader("requestedAmount"),
        valueFormatter: formatCurrency,
        flex: 1,
      },
      {
        field: "requestReason",
        headerName: tHeader("requestReason", "Reason for Request"),
        valueFormatter: (_, row) => {
          return (
            enumTranslations.paymentRequestReason[row.requestReason] ||
            EMPTY_VALUE
          );
        },
      },
      {
        field: "totalOriginalAmount",
        headerName: tHeader("totalOriginalAmount"),
        valueFormatter: formatCurrency,
        flex: 1,
      },
      {
        field: "status",
        headerName: tHeader("status"),
        valueFormatter: (value, row) =>
          typeof value === "string"
            ? enumTranslations.paymentRequestStatus[row.status]
            : value,
        flex: 1,
      },
      {
        field: "requestedOn",
        headerName: tHeader("requestedOn"),
        flex: 1,
        valueFormatter: formatDate,
      },
      {
        field: "invoiceDate",
        headerName: tHeader("invoiceDate"),
        flex: 1,
        valueFormatter: formatDate,
      },
      {
        field: "lastModificationTime",
        headerName: tHeader("lastModificationTime"),
        flex: 1,
        valueFormatter: formatDate,
      },
      { field: "adjusterName", headerName: tHeader("adjusterName"), flex: 1 },
      { field: "insuredName", headerName: tHeader("insuredName"), flex: 1 },
      {
        field: "amountDueFromInsurer",
        headerName: tHeader("amountDueFromInsurer"),
        valueFormatter: formatCurrency,
        flex: 1,
      },
      {
        field: "requestedAmountType",
        headerName: tHeader("requestedAmountType"),
        flex: 1,
        valueFormatter: (_, row) =>
          enumTranslations.paymentRequestRequestedAmountType[
            row.requestedAmountType
          ] || EMPTY_VALUE,
      },
    ];
    if (!isShopUser) {
      columnDefs.push({
        field: "closureStatus",
        headerName: tHeader("closureStatus"),
        valueGetter: (_, row) =>
          enumTranslations.paymentRequestClosureStatus[row.closureStatus] ??
          EMPTY_VALUE,
      });
    }
    return columnDefs;
  }, [
    brandClassification,
    enumTranslations.paymentRequestClosureStatus,
    enumTranslations.paymentRequestRequestedAmountType,
    enumTranslations.paymentRequestStatus,
    enumTranslations.paymentRequestReason,
    formatCurrency,
    formatDate,
    isShopUser,
    tHeader,
    tValue,
  ]);

  const columns: GridColDef[] = useMemo(() => {
    const formattedColumns = initialColumns.map(({ flex, field, ...rest }) => {
      const width = customWidths?.[field];
      return { field, flex: width ? undefined : flex, ...rest, width };
    });

    const sortedColumns = !columnSortOrder
      ? formattedColumns
      : formattedColumns.sort(
          (a, b) =>
            columnSortOrder.indexOf(a.field) - columnSortOrder.indexOf(b.field)
        );
    return sortedColumns;
  }, [columnSortOrder, customWidths, initialColumns]);

  const onColumnOrderChange = useDebouncedCallback<
    GridEventListener<"columnOrderChange">
  >(
    (_params, _event, details: any) => {
      const order = details.api
        .getAllColumns()
        .map(({ field }: { field: any }) => field);
      setColumnSortOrder(order);
    },
    [setColumnSortOrder],
    200
  );

  const onColumnResize = useDebouncedCallback<
    GridEventListener<"columnResize">
  >(
    (params) => {
      const { field } = params.colDef;
      const { width } = params;
      setCustomWidths((current) => {
        const newWidthsObject = { ...current, [field]: width };
        return newWidthsObject;
      });
    },
    [setCustomWidths],
    200
  );

  const onColumnVisibilityModelChange = useCallback<
    NonNullable<DataGridProProps["onColumnVisibilityModelChange"]>
  >(
    (model) => {
      setColumnVisibilityModel(model);
    },
    [setColumnVisibilityModel]
  );

  const resetColumns = useCallback(() => {
    setColumnSortOrder(DEFAULT_COLUMN_SORT_ORDER);
    setCustomWidths(DEFAULT_CUSTOM_COLUMN_WIDTHS);
    setColumnVisibilityModel(DEFAULT_COLUMN_VISIBILITY_MODEL);
  }, [setColumnSortOrder, setColumnVisibilityModel, setCustomWidths]);

  return {
    columns,
    onColumnOrderChange,
    onColumnResize,
    onColumnVisibilityModelChange,
    columnVisibilityModel,
    areColumnsCustomized,
    resetColumns,
  };
}
